<script setup lang="ts">
/**
 * Общее — макет 84:575: название, ключ, описание и блок доступа.
 *
 * Ключ показан и заблокирован — так же считает и сервис: менять его нельзя,
 * поля `key` нет даже в теле `UpdateProject`. На нём висят номера задач и
 * привязка веток GitLab.
 *
 * Из четырёх переключателей макета здесь один — приватность. «Переносить
 * задачи может любой участник», «Требовать оценку перед переводом в работу» и
 * «Разрешить DevOps-действия из карточки» в модели проекта не существуют, а
 * тумблер, который не переживает перезагрузку, врёт убедительнее отсутствия.
 * Архива проектов нет тем же образом (DEV-1500). Всё это — в docs/design-gaps.md.
 */
import { AppSwitch, AppTextField } from '@vinprop/design-system'
import { useI18n } from 'vue-i18n'

import { useSettingsStore } from '../store'

const { t } = useI18n()
const store = useSettingsStore()
</script>

<template>
  <div class="gtab">
    <label class="gtab__field">
      <span class="gtab__label">{{ t('settings.general.name') }}</span>
      <AppTextField v-model="store.form.name" :aria-label="t('settings.general.name')" />
    </label>

    <label class="gtab__field">
      <span class="gtab__label">{{ t('settings.general.key') }}</span>
      <AppTextField
        :model-value="store.project?.key ?? ''"
        disabled
        :aria-label="t('settings.general.key')"
      />
      <span class="gtab__note">{{ t('settings.general.keyNote') }}</span>
    </label>

    <label class="gtab__field">
      <span class="gtab__label">{{ t('settings.general.description') }}</span>
      <AppTextField
        v-model="store.form.description"
        :aria-label="t('settings.general.description')"
      />
    </label>

    <p class="gtab__overline">{{ t('settings.general.access') }}</p>

    <div class="gtab__card">
      <div class="grow">
        <div class="grow__text">
          <span class="grow__name">{{ t('settings.general.private') }}</span>
          <span class="grow__note">{{ t('settings.general.privateNote') }}</span>
        </div>

        <span class="grow__spacer" />

        <AppSwitch
          v-model="store.form.isPrivate"
          :aria-label="t('settings.general.private')"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.gtab {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.gtab__field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 520px;
  max-width: 100%;
}

.gtab__label,
.gtab__overline {
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-overline);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.gtab__note {
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.gtab__card {
  display: flex;
  flex-direction: column;
  margin-top: -12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.grow {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 56px;
  padding: 0 16px;
  border-top: 1px solid var(--color-border);
}

.grow:first-child {
  border-top: none;
}

.grow__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.grow__name {
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
}

.grow__note {
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.grow__spacer {
  flex: 1;
}
</style>
